import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

export interface TableFormItem {
  item: string; // Item name (e.g., "Wine", "Liquor")
  fieldValues: Record<string, any>; // Field label -> value for this item
}

export interface FormCompletion {
  id?: string;
  formId: string;
  formName: string;
  locationId: string;
  locationName: string;
  assetId?: string; // Asset ID when form is run from asset page
  variableValues?: Record<string, string>; // variable name -> value
  fieldValues: Record<string, any>; // field label -> value (form data) - for standard forms
  // For table forms:
  isTableForm?: boolean; // Flag to indicate this is a table form completion
  headerValues?: Record<string, any>; // Header field values (like Date) that apply to all items
  tableItems?: TableFormItem[]; // Array of items with their field values
  aiSummary?: string; // AI-generated summary of the form completion
  completedBy?: string; // User ID
  completedByName?: string; // Formatted display name (e.g., "Name (email)")
  completionDate?: any;
  createdAt?: any;
  organizationId?: string;
  owner_role?: string; // Owner role from compliance template for access control
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export const formCompletionService = {
  // Add a form completion record
  addCompletion: async (completionData: Omit<FormCompletion, 'id' | 'completionDate' | 'createdAt'>) => {
    try {
      const completionsRef = collection(db, 'formCompletions');
      // Clean undefined values before saving to Firestore (but don't include timestamps yet)
      const cleanedData = removeUndefined({
        ...completionData,
        // Trim whitespace from locationName if provided
        ...(completionData.locationName && { locationName: completionData.locationName.trim() }),
        // For table forms, ensure structure is clean
        ...(completionData.isTableForm && {
          isTableForm: true,
          headerValues: completionData.headerValues || {},
          tableItems: completionData.tableItems || [],
          // Keep fieldValues empty for table forms (or include summary for backward compatibility)
          fieldValues: {}
        }),
        // For standard forms, ensure isTableForm is false or undefined
        ...(!completionData.isTableForm && {
          isTableForm: false,
          fieldValues: completionData.fieldValues || {}
        })
      });
      
      // Add timestamps after cleaning to ensure serverTimestamp() is not affected
      const dataToSave = {
        ...cleanedData,
        completionDate: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      // Log the data being saved (for debugging)
      console.log('💾 Saving form completion to Firestore:', {
        formId: dataToSave.formId,
        formName: dataToSave.formName,
        isTableForm: dataToSave.isTableForm || false,
        hasAiSummary: !!dataToSave.aiSummary,
        aiSummary: dataToSave.aiSummary ? dataToSave.aiSummary.substring(0, 100) + '...' : 'None',
        fieldValuesCount: dataToSave.fieldValues ? Object.keys(dataToSave.fieldValues).length : 0,
        tableItemsCount: dataToSave.tableItems ? dataToSave.tableItems.length : 0,
        hasHeaderValues: !!dataToSave.headerValues,
        hasCompletionDate: !!dataToSave.completionDate,
        hasCreatedAt: !!dataToSave.createdAt
      });
      
      const docRef = await addDoc(completionsRef, dataToSave);
      console.log('✅ Form completion saved with ID:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('❌ Error adding form completion:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      return { success: false, error: error.message };
    }
  },

  // Get all completions for a specific form
  getCompletionsForForm: async (formId: string) => {
    try {
      const completionsRef = collection(db, 'formCompletions');
      const q = query(
        completionsRef,
        where('formId', '==', formId)
      );
      const querySnapshot = await getDocs(q);

      const completions: FormCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as FormCompletion);
      });

      // Sort by completionDate in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completionDate?.toDate ? a.completionDate.toDate().getTime() : a.completionDate;
        const dateB = b.completionDate?.toDate ? b.completionDate.toDate().getTime() : b.completionDate;
        return (dateB || 0) - (dateA || 0);
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting form completions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all form completions
  getAllCompletions: async () => {
    try {
      const completionsRef = collection(db, 'formCompletions');
      const querySnapshot = await getDocs(completionsRef);

      const completions: FormCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as FormCompletion);
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting all form completions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get form completions by location
  getCompletionsByLocation: async (locationId: string) => {
    try {
      const completionsRef = collection(db, 'formCompletions');
      const q = query(
        completionsRef,
        where('locationId', '==', locationId)
      );
      const querySnapshot = await getDocs(q);

      const completions: FormCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as FormCompletion);
      });

      // Sort by completionDate in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completionDate?.toDate ? a.completionDate.toDate().getTime() : a.completionDate;
        const dateB = b.completionDate?.toDate ? b.completionDate.toDate().getTime() : b.completionDate;
        return (dateB || 0) - (dateA || 0);
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting form completions by location:', error);
      return { success: false, error: error.message };
    }
  },

  // Get form completions for a specific asset
  getCompletionsForAsset: async (assetId: string) => {
    try {
      const completionsRef = collection(db, 'formCompletions');
      const querySnapshot = await getDocs(completionsRef);

      const completions: FormCompletion[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const completion = { id: doc.id, ...data } as FormCompletion;
        
        // Check if assetId matches in various possible locations
        const matchesAsset = 
          completion.assetId === assetId ||
          (completion.fieldValues && completion.fieldValues['assetId'] === assetId) ||
          (completion.fieldValues && completion.fieldValues['asset'] === assetId);
        
        if (matchesAsset) {
          completions.push(completion);
        }
      });

      // Sort by completionDate in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completionDate?.toDate ? a.completionDate.toDate().getTime() : a.completionDate;
        const dateB = b.completionDate?.toDate ? b.completionDate.toDate().getTime() : b.completionDate;
        return (dateB || 0) - (dateA || 0);
      });

      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting form completions for asset:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a form completion (e.g., to add AI summary)
  updateCompletion: async (completionId: string, updates: Partial<FormCompletion>) => {
    try {
      const completionRef = doc(db, 'formCompletions', completionId);
      const cleanedData = removeUndefined(updates);
      await updateDoc(completionRef, cleanedData);
      console.log('✅ Form completion updated:', completionId);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error updating form completion:', error);
      return { success: false, error: error.message };
    }
  },
};

